/**
 * NovaFeedInsert — Nova as a standalone post in the main chapter feed.
 * Demo routes: unified Nova block. Else: legacy layout.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import NovaAvatarImg, { novaAvatarChapterFromSlug } from './NovaAvatarImg';
import { NovaOrb, NovaContextPill, NOVA_UNIFIED_GRADIENT } from './NovaUnified';
import { getCircuitBreakerSuggestions } from '../data/novaCommunityConfig';

const CHAPTER_THEMES = {
  fertility: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  ttc: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  pregnancy: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  motherhood: { accent: '#9B2942', stripBg: 'linear-gradient(140deg, #FBEAF0, #F5EAED)', accentBg: '#F5EAED' },
  postpartum: { accent: '#9B2942', stripBg: 'linear-gradient(140deg, #FBEAF0, #F5EAED)', accentBg: '#F5EAED' },
  menopause: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  cycle: { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
  'first-chapter': { accent: '#D4537E', stripBg: 'linear-gradient(140deg, #FBEAF0, #f5f0f5)', accentBg: '#FBEAF0' },
};

function feedInsertContextPill(isCircuitBreaker, tag) {
  if (isCircuitBreaker) return 'Taking a breath';
  if (tag && String(tag).trim()) {
    const u = String(tag).trim();
    if (u === u.toUpperCase() && u.length <= 12) {
      return u.charAt(0) + u.slice(1).toLowerCase();
    }
    return u;
  }
  return 'Guidance';
}

export function NovaFeedInsert({
  theme,
  chapter = 'fertility',
  insertType,
  headline,
  body,
  tag,
  cta,
  onOpenNova,
}) {
  const { pathname } = useLocation();
  const tagFontSize = pathname.startsWith('/demo/') ? 10 : 9;
  const isCircuitBreaker = insertType === 'circuit_breaker';
  const t = theme || CHAPTER_THEMES[chapter] || CHAPTER_THEMES.fertility;
  const novaChapter = novaAvatarChapterFromSlug(chapter);
  const isDemo = pathname.startsWith('/demo/');

  if (isDemo) {
    const pill = feedInsertContextPill(isCircuitBreaker, tag);
    return (
      <div
        style={{
          borderRadius: 10,
          padding: '10px 12px',
          marginBottom: 10,
          background: NOVA_UNIFIED_GRADIENT,
          border: '0.5px solid #f4c0d1',
          borderLeft: '3px solid #993556',
          overflow: 'hidden',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
        }}
      >
        <NovaOrb size="medium" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#993556' }}>Nova</span>
            <NovaContextPill>{pill}</NovaContextPill>
          </div>

          {!isCircuitBreaker && headline && (
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>{headline}</p>
          )}

          <p
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              color: isCircuitBreaker ? 'rgba(60,30,45,0.7)' : '#4b1528',
              fontStyle: isCircuitBreaker ? 'italic' : 'normal',
              margin: 0,
            }}
          >
            {body}
          </p>

          {isCircuitBreaker && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {getCircuitBreakerSuggestions(chapter).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#2d1a22' }}>
                  <span style={{ flexShrink: 0 }}>{s.emoji}</span>
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
            {cta && (
              <button
                type="button"
                onClick={cta.onClick}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#993556',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                {cta.label}
              </button>
            )}
            <button
              type="button"
              onClick={onOpenNova}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 11,
                color: 'rgba(60,30,45,0.5)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2d1a22';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(60,30,45,0.5)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Talk to Nova →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 14,
        padding: 16,
        marginBottom: 10,
        background: isCircuitBreaker ? 'var(--white)' : t.stripBg,
        border: isCircuitBreaker ? '1px solid var(--border, #eee)' : '0.5px solid rgba(212,83,126,0.15)',
        overflow: 'hidden',
        borderLeft: '2px solid rgba(212,83,126,0.2)',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0, marginTop: 2 }}>
          <NovaAvatarImg size={32} alt="" chapter={novaChapter} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#993556' }}>Nova</span>
            {isCircuitBreaker ? <NovaContextPill>Taking a breath</NovaContextPill> : null}
            {!isCircuitBreaker && tag && (
              <span
                style={{
                  fontSize: tagFontSize,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: t.accentBg,
                  color: t.accent,
                  border: '0.5px solid rgba(212,83,126,0.2)',
                }}
              >
                {tag}
              </span>
            )}
          </div>

          {!isCircuitBreaker && headline && (
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>{headline}</p>
          )}

          <p
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: isCircuitBreaker ? 'rgba(60,30,45,0.7)' : '#2d1a22',
              fontStyle: isCircuitBreaker ? 'italic' : 'normal',
            }}
          >
            {body}
          </p>

          {isCircuitBreaker && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {getCircuitBreakerSuggestions(chapter).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#2d1a22' }}>
                  <span style={{ flexShrink: 0 }}>{s.emoji}</span>
                  <span>{s.text}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
            {cta && (
              <button
                type="button"
                onClick={cta.onClick}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: t.accent,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
              >
                {cta.label}
              </button>
            )}
            <button
              type="button"
              onClick={onOpenNova}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 11,
                color: 'rgba(60,30,45,0.5)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2d1a22';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(60,30,45,0.5)';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Talk to Nova →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NovaFeedInsert;
